import { useState, useCallback, useEffect } from 'react';

interface Message {
  role: 'user' | 'assistant';
  content: string;
  isRich?: boolean;
  type?: string;
}

/**
 * Hook to manage AI Assistant interactions in the storefront.
 * Respects the global 'isAssistantEnabled' flag.
 */
export function useStorefrontAi() {
  const [messages, setMessages] = useState<Message[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isEnabled, setIsEnabled] = useState(false);
  const [isInitializing, setIsInitializing] = useState(true);

  // Fetch initial configuration
  useEffect(() => {
    async function checkEnabled() {
      try {
        const response = await fetch('/api/configuration/public');
        if (response.ok) {
          const data = await response.json();
          setIsEnabled(data.integration?.isAssistantEnabled ?? false);
        }
      } catch (error) {
        console.error('Error fetching AI configuration:', error);
        setIsEnabled(false);
      } finally {
        setIsInitializing(false);
      }
    }
    checkEnabled();
  }, []);

  const sendMessage = useCallback(async (content: string) => {
    if (!content.trim() || !isEnabled) return;

    const userMessage: Message = { role: 'user', content };
    setMessages(prev => [...prev, userMessage]);
    setIsLoading(true);

    try {
      const getCsrfToken = () => {
        const match = document.cookie.match(/XSRF-TOKEN=([^;]+)/);
        return match ? decodeURIComponent(match[1]) : '';
      };

      const response = await fetch('/api/ai/assistant/chat', {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'X-CSRF-Token': getCsrfToken()
        },
        body: JSON.stringify({ 
          messages: [...messages, userMessage] 
        }),
      });

      if (!response.ok) throw new Error('Error en el asistente');

      const reader = response.body?.getReader();
      if (!reader) throw new Error('No stream available');

      const botMessage: Message = { role: 'assistant', content: '' };
      setMessages(prev => [...prev, botMessage]);

      const decoder = new TextDecoder();
      let done = false;
      while (!done) {
        const { value, done: doneReading } = await reader.read();
        done = doneReading;
        if (value) {
          const chunkValue = decoder.decode(value);
          setMessages(prev => {
            const newMessages = [...prev];
            const lastIdx = newMessages.length - 1;
            newMessages[lastIdx] = {
              ...newMessages[lastIdx],
              content: newMessages[lastIdx].content + chunkValue
            };
            return newMessages;
          });
        }
      }
    } catch (error) {
      console.error('AI Error:', error);
      setMessages(prev => [...prev, { 
        role: 'assistant', 
        content: 'Lo siento, ocurrió un error al procesar tu mensaje.' 
      }]);
    } finally {
      setIsLoading(false);
    }
  }, [messages, isEnabled]);

  const clearChat = useCallback(() => {
    setMessages([]);
  }, []);

  return {
    messages,
    isLoading,
    isEnabled,
    isInitializing,
    sendMessage,
    clearChat
  };
}
